import { createVitalityReader, hydrateVitality, selectInventoryVitalitySource } from '../compile/vitality-reader.js';
import { readInventoryField } from '../compile/inventory-raw-readers.js';
import { record, upper, type Row } from '../ability/context.js';

const readVitality = createVitalityReader({ resolveInventoryInstance: () => null });
const vitality = (row: Row | null): Row | null => record(record(row?.computed)?.vitality);
/** The hydration display floor of one never creates life for an object with no positive maximum. */
const hydrateObjectVitalityState = (source: Row): Row => {
  const value = hydrateVitality(source) as Row;
  const maximum = Number(value.v_max ?? value.base_v_max ?? 0);
  const extra = Number(value.v_max_temp ?? value.base_v_max_temp ?? 0);
  if (maximum > 0 || maximum + extra > 0) return value;
  return hydrateVitality({ base_v_max: 0, base_v_current: 0,
    base_v_temp: value.base_v_temp ?? value.v_temp ?? 0, base_v_max_temp: extra }) as Row;
};
const catalogDefinition = (catalog: unknown, id: string): Row | null => {
  const rows = record(catalog);
  return record(rows?.[id] ?? rows?.[id.toLowerCase()]);
};

/** A partial cache definition cannot shadow a held template's vitality. */
export function objectVitalityTemplate(row: Row, encounter: Row | null, definition: (id: string) => Row | null): Row | null {
  const id = upper(readInventoryField(row, 'inventory_item_id'));
  for (const candidate of [catalogDefinition(row.catalog, id), catalogDefinition(encounter?.catalog, id), definition(id)])
    if (vitality(candidate)) return candidate;
  return null;
}

/** Shared floor-object reader. Unknown facts stay unknown until a successful raw/template read.
 * Raw 0/0 inherits a positive template max; positive authored max preserves current, including zero.
 */
export function resolveObjectVitality(row: Row, encounter: Row | null, definition: (id: string) => Row | null,
  factsRead = false): Row | null {
  const template = objectVitalityTemplate(row, encounter, definition);
  const own = vitality(row);
  const defaults = vitality(template);
  const raw = record(row.inventory);
  const maximumHeld = defaults || own && ('base_v_max' in own || 'v_max' in own)
    || raw && Number(readInventoryField(row, 'v_max')) > 0;
  // A lean current-only override still needs its template maximum before it can be struck.
  if (!maximumHeld) return factsRead ? hydrateObjectVitalityState({ base_v_max: 0, base_v_current: 0,
    base_v_temp: readInventoryField(row, 'v_temp') ?? own?.base_v_temp ?? own?.v_temp ?? 0 }) : null;
  // Derived template fields must not override a lean instance's base current.
  const bases: Row = {};
  for (const key of ['v_max', 'v_current', 'v_temp', 'v_max_temp']) {
    const value = defaults?.[`base_${key}`] ?? defaults?.[key];
    if (value !== undefined) bases[`base_${key}`] = value;
  }
  if (raw && Object.prototype.hasOwnProperty.call(raw, 'v_max')) {
    const columns = Object.fromEntries(['v_max', 'v_current', 'v_temp'].map(key => [key, readInventoryField(row, key)]));
    if (!template && own && !(Number(columns.v_max) > 0)) return hydrateObjectVitalityState(own);
    const maxTemp = own?.base_v_max_temp ?? own?.v_max_temp;
    if (maxTemp !== undefined) bases.base_v_max_temp = maxTemp;
    const selected = record(selectInventoryVitalitySource(columns, template ?? {}, readVitality))!;
    if (!(Number(selected.v_max) > 0)) selected.v_current = 0;
    for (const key of ['v_max', 'v_current', 'v_temp']) bases[`base_${key}`] = selected[key] ?? 0;
    return hydrateObjectVitalityState(bases);
  }
  if (defaults || own) {
    const selected = { ...bases };
    for (const key of ['v_max', 'v_current', 'v_temp', 'v_max_temp']) {
      const value = own?.[`base_${key}`] ?? own?.[key];
      if (value !== undefined) selected[`base_${key}`] = value;
    }
    // Lean rows omit their default maximum. Explicit instance values, including
    // current=0, still outrank the definition's corresponding base fields.
    if (raw) for (const key of ['v_current', 'v_temp'])
      if (Object.prototype.hasOwnProperty.call(raw, key)) selected[`base_${key}`] = readInventoryField(row, key);
    return hydrateObjectVitalityState(selected);
  }
  return factsRead ? hydrateObjectVitalityState({ base_v_max: 0, base_v_current: 0 }) : null;
}

export function installObjectVitality(row: Row, value: Row): void {
  row.computed = { ...record(row.computed), vitality: value };
}
